import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  type ButtonProps,
} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Email from '@mui/icons-material/Email';
import Send from '@mui/icons-material/Send';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  CreateInvitationDocument,
  CreateOrganizationAddressDocument,
  GetCurrentTenantDocument,
  GetOrganizationsForAddressListDocument,
  MarkInvitationFailedDocument,
  UpdateOrganizationAddressDocument,
  UpdateTenantResellerLogoDocument,
  type GetCurrentTenantQuery,
  type GetOrganizationsForAddressListQuery,
} from '@/generated/root-types';
import { SendMailDocument } from '@/generated/pos-types';
import { useNotification } from '@/app/contexts/NotificationContext';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  buildInviteContactBlock,
  buildRegistrationLink,
  contactFormToAddressInput,
  contactToForm,
  CUSTOMER_INVITE_EMAIL_TEMPLATE,
  dataUriToInlineLogoAttachment,
  DEFAULT_ORDA_LOGO_SRC,
  emptyContactForm,
  formsEqual,
  isValidEmail,
  MAX_LOGO_FILE_BYTES,
  pickCustomerContactAddress,
  type CustomerContactForm,
} from './customerInviteUtils';

export type CustomerInviteDialogProps = {
  open: boolean;
  onClose: () => void;
};

export default function CustomerInviteDialog({ open, onClose }: CustomerInviteDialogProps) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [inviteTab, setInviteTab] = useState(0);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState<CustomerContactForm>(emptyContactForm);
  const [savedContactForm, setSavedContactForm] = useState<CustomerContactForm>(emptyContactForm);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [savedLogo, setSavedLogo] = useState<string | null>(null);
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [inviteeMessage, setInviteeMessage] = useState('');

  const { data: tenantData, loading: tenantLoading } = useQuery<GetCurrentTenantQuery>(
    GetCurrentTenantDocument,
    {
      skip: !open,
    },
  );

  const tenantId = tenantData?.currentTenant?.id;
  const savedLogoFromServer = tenantData?.currentTenant?.resellerLogo ?? null;

  const {
    data: orgsData,
    loading: orgsLoading,
    refetch: refetchOrgs,
  } = useQuery<GetOrganizationsForAddressListQuery>(GetOrganizationsForAddressListDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE, tenantId: tenantId ?? null },
    skip: !open || !tenantId,
  });

  const organizations = orgsData?.organizations?.nodes ?? [];
  const activeOrgId =
    selectedOrgId ?? organizations.find((o) => o.isDefault)?.id ?? organizations[0]?.id ?? null;
  const activeOrg = organizations.find((o) => o.id === activeOrgId) ?? null;
  const savedContactAddress = pickCustomerContactAddress(activeOrg?.addresses);

  const registrationLink = tenantId ? buildRegistrationLink(tenantId) : '';
  const hasPersistedContact = savedContactAddress != null;
  const isContactDirty = !formsEqual(contactForm, savedContactForm);
  const canSendMail = hasPersistedContact && !isContactDirty && isValidEmail(inviteeEmail);
  const loading = tenantLoading || orgsLoading;

  const [createAddress, { loading: creatingContact }] = useMutation(
    CreateOrganizationAddressDocument,
  );

  const [updateAddress, { loading: updatingContact }] = useMutation(
    UpdateOrganizationAddressDocument,
  );

  const [updateLogo] = useMutation(UpdateTenantResellerLogoDocument);

  const [sendMail, { loading: sendingMail }] = useMutation(SendMailDocument);
  const [createInvitation, { loading: creatingInvitation }] = useMutation(CreateInvitationDocument);
  const [markInvitationFailed] = useMutation(MarkInvitationFailedDocument);

  useEffect(() => {
    if (!open) {
      setInviteTab(0);
      setSelectedOrgId(null);
      setInviteeEmail('');
      setInviteeName('');
      setInviteeMessage('');
      return;
    }
    setSavedLogo(savedLogoFromServer);
    setLogoPreview(savedLogoFromServer);
  }, [open, savedLogoFromServer]);

  useEffect(() => {
    if (!open) return;
    const form = contactToForm(savedContactAddress);
    setContactForm(form);
    setSavedContactForm(form);
  }, [open, activeOrgId, savedContactAddress]);

  const updateContactField = (field: keyof CustomerContactForm, value: string) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoSelect = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError(t('customers.invite.logoInvalidType'));
      return;
    }
    if (file.size > MAX_LOGO_FILE_BYTES) {
      showError(t('customers.invite.logoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
    handleSaveLogo();
  };

  const handleSaveLogo = async () => {
    if (!tenantId) return;
    try {
      const res = await updateLogo({
        variables: { input: { tenantId, resellerLogo: logoPreview } },
      });
      const payload = res.data?.updateTenantResellerLogo;
      const err = errorsFromPayload(payload);
      if (err) {
        showError(err);
        return;
      }
      const nextLogo = payload?.tenant?.resellerLogo ?? logoPreview;
      setSavedLogo(nextLogo);
      setLogoPreview(nextLogo);
      showSuccess(t('customers.invite.logoSaved'));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleSaveContact = async () => {
    if (!activeOrgId) {
      showError(t('customers.invite.noOrganization'));
      return;
    }
    try {
      const addressInput = contactFormToAddressInput(contactForm);
      if (savedContactAddress) {
        const res = await updateAddress({
          variables: { input: { id: savedContactAddress.id, ...addressInput } },
        });
        const err = errorsFromPayload(res.data?.updateOrganizationAddress);
        if (err) {
          showError(err);
          return;
        }
      } else {
        const res = await createAddress({
          variables: {
            input: {
              organizationId: activeOrgId,
              type: 'CUSTOMER_CONTACT',
              isDefault: true,
              ...addressInput,
            },
          },
        });
        const err = errorsFromPayload(res.data?.createOrganizationAddress);
        if (err) {
          showError(err);
          return;
        }
      }
      setSavedContactForm({ ...contactForm });
      await refetchOrgs();
      showSuccess(t('customers.invite.contactSaved'));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleCopyLink = async () => {
    if (!registrationLink) return;
    try {
      await navigator.clipboard.writeText(registrationLink);
      showSuccess(t('customers.invite.linkCopied'));
    } catch {
      showError(t('customers.invite.linkCopyFailed'));
    }
  };

  const handleSendMail = async () => {
    if (!canSendMail || !tenantId) return;
    const logoAttachment = dataUriToInlineLogoAttachment(savedLogo || DEFAULT_ORDA_LOGO_SRC);
    if (!logoAttachment) {
      showError(t('customers.invite.logoInvalidType'));
      return;
    }

    const invitationRes = await createInvitation({
      variables: {
        input: {
          recipientEmail: inviteeEmail.trim(),
          recipientName: inviteeName.trim() || undefined,
          message: inviteeMessage.trim() || undefined,
        },
      },
    });
    const invitation = invitationRes.data?.createInvitation?.invitation;
    if (!invitation) {
      showError(t('customers.invite.emailFailed'));
      return;
    }

    const organizationName = activeOrg?.name ?? '';
    const contactBlock = buildInviteContactBlock({
      organizationName,
      form: savedContactForm,
      phoneLabel: t('organizationDetail.phone'),
      emailLabel: t('organizationDetail.email'),
    });
    const inviteLink = buildRegistrationLink(tenantId, invitation.token);

    try {
      const res = await sendMail({
        variables: {
          sendMail: {
            to: [inviteeEmail.trim()],
            subject: t('customers.invite.emailSubject', { organizationName }),
            templateName: CUSTOMER_INVITE_EMAIL_TEMPLATE,
            isBodyHtml: true,
            bodyMappings: [
              { key: '#TITLE#', value: t('customers.invite.emailTitle') },
              { key: '#HEADING#', value: t('customers.invite.emailHeading', { organizationName }) },
              { key: '#SUBTITLE#', value: t('customers.invite.emailSubtitle') },
              { key: '#REGISTRATION_LINK#', value: inviteLink },
              { key: '#BUTTON_LABEL#', value: t('customers.invite.emailLinkLabel') },
              { key: '#CONTACT_HEADING#', value: t('customers.invite.emailContactHeading') },
              { key: '#CONTACT_BLOCK#', value: contactBlock },
              { key: '#MESSAGE#', value: inviteeMessage.trim() },
            ],
            attachments: [
              {
                fileName: logoAttachment.fileName,
                mimeType: logoAttachment.mimeType,
                content: logoAttachment.content,
                contentId: logoAttachment.contentId,
              },
            ],
          },
        },
        context: { schema: 'PosSchema' },
      });
      if (!res.data?.sendMail?.isSuccess) {
        await markInvitationFailed({ variables: { input: { id: invitation.id } } });
        showError(res.data?.sendMail?.message ?? t('customers.invite.emailFailed'));
        return;
      }
      showSuccess(t('customers.invite.emailSent'));
      setInviteeEmail('');
      setInviteeName('');
      setInviteeMessage('');
    } catch (e) {
      await markInvitationFailed({ variables: { input: { id: invitation.id } } });
      showError(e instanceof Error ? e.message : t('customers.invite.emailFailed'));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="customer-invite-title"
    >
      <DialogTitle id="customer-invite-title">{t('customers.invite.dialogTitle')}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: 1,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Tabs
          value={inviteTab}
          onChange={(_, v) => setInviteTab(v)}
          aria-label={t('customers.invite.tabsAria')}
        >
          <Tab label={t('customers.invite.tabEmail')} />
          <Tab label={t('customers.invite.tabLink')} />
        </Tabs>

        {inviteTab === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Card
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                p: 2,
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6">{t('customers.invite.title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('customers.invite.subtitle')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  component="img"
                  src={logoPreview || DEFAULT_ORDA_LOGO_SRC}
                  alt=""
                  sx={{
                    maxWidth: 160,
                    maxHeight: 80,
                    objectFit: 'contain',
                    alignSelf: { xs: 'center', sm: 'flex-start' },
                  }}
                />
                <Box
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}
                >
                  <Typography variant="subtitle2">{t('customers.invite.logoTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('customers.invite.logoHint')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      hidden
                      onChange={(e) => handleLogoSelect(e.target.files?.[0] ?? null)}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('customers.invite.logoUpload')}
                    </Button>
                  </Box>
                </Box>
              </Box>

              {loading ? (
                <Typography color="text.secondary">{t('customers.invite.loading')}</Typography>
              ) : organizations.length === 0 ? (
                <Alert severity="warning">{t('customers.invite.noOrganizations')}</Alert>
              ) : (
                <>
                  <TextField
                    select
                    label={t('customers.invite.organization')}
                    value={activeOrgId ?? ''}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    fullWidth
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                        {org.isDefault ? ` (${t('common.default')})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>

                  {!hasPersistedContact ? (
                    <Alert severity="info">{t('customers.invite.noContactBanner')}</Alert>
                  ) : null}

                  {!hasPersistedContact && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      <TextField
                        label={t('orgAddresses.line1')}
                        value={contactForm.line1}
                        onChange={(e) => updateContactField('line1', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={t('orgAddresses.line2')}
                        value={contactForm.line2}
                        onChange={(e) => updateContactField('line2', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={t('orgAddresses.city')}
                        value={contactForm.city}
                        onChange={(e) => updateContactField('city', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={t('orgAddresses.zip')}
                        value={contactForm.zip}
                        onChange={(e) => updateContactField('zip', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={t('orgAddresses.country')}
                        value={contactForm.country}
                        onChange={(e) => updateContactField('country', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={t('organizationDetail.phone')}
                        value={contactForm.phone}
                        onChange={(e) => updateContactField('phone', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        label={t('customers.invite.contactEmail')}
                        value={contactForm.email}
                        onChange={(e) => updateContactField('email', e.target.value)}
                        fullWidth
                        sx={{ gridColumn: { sm: '1 / -1' } }}
                      />
                    </Box>
                  )}
                  {!hasPersistedContact && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={() => void handleSaveContact()}
                        disabled={
                          !activeOrgId || creatingContact || updatingContact || !isContactDirty
                        }
                      >
                        {t('common.save')}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Card>
            <Typography variant="subtitle1">{t('customers.invite.emailSectionTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customers.invite.emailSectionHint')}
            </Typography>
            <TextField
              type="email"
              label={t('customers.invite.inviteeEmail')}
              helperText={t('customers.invite.inviteeEmailHint')}
              value={inviteeEmail}
              onChange={(e) => setInviteeEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('customers.invite.customerName')}
              helperText={t('customers.invite.customerNameHint')}
              value={inviteeName}
              onChange={(e) => setInviteeName(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('customers.invite.message')}
              helperText={t('customers.invite.messageHint')}
              value={inviteeMessage}
              onChange={(e) => setInviteeMessage(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle1">{t('customers.invite.linkSectionTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customers.invite.linkSectionHint')}
            </Typography>
            <TextField
              value={registrationLink}
              fullWidth
              slotProps={{ input: { readOnly: true } }}
              multiline
              minRows={2}
            />
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => void handleCopyLink()}
              disabled={!registrationLink}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('customers.invite.copyLink')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'between' }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        {inviteTab === 0 ? (
          <Button
            variant="contained"
            startIcon={<Email />}
            onClick={() => void handleSendMail()}
            disabled={!canSendMail || sendingMail || creatingInvitation}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('customers.invite.sendEmail')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export type CustomerInviteButtonProps = {
  label?: string;
  showIcon?: boolean;
  isLarge?: boolean;
} & Omit<ButtonProps, 'onClick' | 'children'>;

export function CustomerInviteButton({
  label,
  showIcon = true,
  variant = 'contained',
  isLarge = false,
  ...buttonProps
}: CustomerInviteButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant={variant}
        onClick={() => setOpen(true)}
        {...buttonProps}
        sx={{
          height: !isLarge ? '100%' : '64px',
          width: isLarge ? '100%' : undefined,
          fontSize: isLarge ? '1rem' : undefined,
          gap: isLarge ? 1 : undefined,
          animation: isLarge ? 'pulse 2s infinite' : undefined,
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.8, scale: 1 },
            '50%': { opacity: 1, scale: 1.02 },
          },
        }}
      >
        {label ?? t('customers.inviteCustomer')}
        {showIcon ? <Send sx={{ ml: 1 }} /> : null}
      </Button>
      <CustomerInviteDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
